import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Eye, EyeOff, PlaneTakeoff, Route } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency } from "../../lib/format";

const blank = {
    title: "",
    fromLocation: "",
    toLocation: "",
    category: "airport-hotel",
    distanceKm: 50,
    durationMin: 90,
    sedanPrice: 1500,
    suvPrice: 2000,
    tempoPrice: 3500,
    image: "",
    description: "",
    features: [],
    active: true,
};

const ManageTransfers = () => {
    const { transfers, addTransfer, updateTransfer, deleteTransfer } = useData();
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blank);
    const [filterCat, setFilterCat] = useState("All");

    const filtered = useMemo(() => {
        if (filterCat === "All") return transfers;
        return transfers.filter((t) => t.category === filterCat);
    }, [transfers, filterCat]);

    const open = (t = null) => {
        if (t) {
            setEditing(t.id);
            setForm({ ...blank, ...t });
        } else {
            setEditing("new");
            setForm(blank);
        }
    };
    const close = () => {
        setEditing(null);
        setForm(blank);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title) return toast.error("Title is required.");
        try {
            if (editing === "new") {
                await addTransfer(form);
                toast.success("Transfer added.");
            } else {
                await updateTransfer(editing, form);
                toast.success("Transfer updated.");
            }
            close();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save.");
        }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this transfer?")) return;
        try {
            await deleteTransfer(id);
            toast.success("Transfer deleted.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete.");
        }
    };
    const toggleActive = (t) => updateTransfer(t.id, { active: !t.active });

    return (
        <div data-testid="admin-transfers-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl text-ink">Manage Transfers</h1>
                    <p className="text-ink-light text-sm mt-1">{transfers.length} fixed-price routes</p>
                </div>
                <button onClick={() => open()} className="px-4 py-2.5 rounded-xl bg-saffron-500 hover:bg-saffron-600 text-white font-semibold flex items-center gap-2 shadow-md" data-testid="admin-transfer-add">
                    <Plus className="w-4 h-4" /> Add transfer
                </button>
            </div>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
                {["All", "airport-hotel", "inter-city"].map((c) => (
                    <button
                        key={c}
                        onClick={() => setFilterCat(c)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1 ${
                            filterCat === c
                                ? "bg-saffron-500 border-saffron-500 text-white"
                                : "border-slate-200 text-ink-light hover:border-saffron-300"
                        }`}
                    >
                        {c === "airport-hotel" ? <PlaneTakeoff className="w-3 h-3" /> : c === "inter-city" ? <Route className="w-3 h-3" /> : null}
                        {c === "All" ? "All" : c}
                    </button>
                ))}
            </div>

            <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs uppercase tracking-wider text-ink-light">
                                <th className="px-4 py-3 font-semibold">Transfer</th>
                                <th className="px-4 py-3 font-semibold">Category</th>
                                <th className="px-4 py-3 font-semibold">Distance</th>
                                <th className="px-4 py-3 font-semibold">Duration</th>
                                <th className="px-4 py-3 font-semibold">Sedan / SUV / Tempo</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((t) => (
                                <tr key={t.id} className="hover:bg-slate-50" data-testid={`admin-transfer-${t.id}`}>
                                    <td className="px-4 py-3 max-w-md">
                                        <div className="flex items-center gap-3">
                                            <img src={t.image} alt="" className="w-12 h-9 rounded-md object-cover bg-slate-100" />
                                            <div>
                                                <div className="font-semibold text-ink line-clamp-1">{t.title}</div>
                                                <div className="text-xs text-ink-light">{t.fromLocation} → {t.toLocation}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-ink-light text-xs capitalize">{t.category}</td>
                                    <td className="px-4 py-3 text-ink-light">{t.distanceKm} km</td>
                                    <td className="px-4 py-3 text-ink-light">{t.durationMin} min</td>
                                    <td className="px-4 py-3 font-semibold text-saffron-600 whitespace-nowrap">
                                        ₹{t.sedanPrice.toLocaleString("en-IN")} / ₹{t.suvPrice.toLocaleString("en-IN")} / {t.tempoPrice ? `₹${t.tempoPrice.toLocaleString("en-IN")}` : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <button onClick={() => toggleActive(t)} className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${t.active ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-ink-light"}`}>
                                            {t.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                            {t.active ? "Live" : "Hidden"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => open(t)} className="p-2 rounded-md hover:bg-slate-100 text-ink-light"><Edit3 className="w-4 h-4" /></button>
                                        <button onClick={() => remove(t.id)} className="p-2 rounded-md hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={close}>
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-2xl text-ink">{editing === "new" ? "Add transfer" : "Edit transfer"}</h2>
                            <button onClick={close} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <Field label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Cochin Airport (COK) → Fort Kochi" />
                                </div>
                                <Field label="From location" value={form.fromLocation} onChange={(v) => setForm({ ...form, fromLocation: v })} />
                                <Field label="To location" value={form.toLocation} onChange={(v) => setForm({ ...form, toLocation: v })} />
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Category</label>
                                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bare mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white">
                                        <option value="airport-hotel">Airport ↔ Hotel</option>
                                        <option value="inter-city">Inter-city</option>
                                    </select>
                                </div>
                                <Field label="Distance (km)" type="number" value={form.distanceKm} onChange={(v) => setForm({ ...form, distanceKm: Number(v) })} />
                                <Field label="Duration (min)" type="number" value={form.durationMin} onChange={(v) => setForm({ ...form, durationMin: Number(v) })} />
                                <Field label="Sedan price (₹)" type="number" value={form.sedanPrice} onChange={(v) => setForm({ ...form, sedanPrice: Number(v) })} />
                                <Field label="SUV price (₹)" type="number" value={form.suvPrice} onChange={(v) => setForm({ ...form, suvPrice: Number(v) })} />
                                <Field label="Tempo price (₹, optional)" type="number" value={form.tempoPrice || ""} onChange={(v) => setForm({ ...form, tempoPrice: v === "" ? null : Number(v) })} />
                                <Field label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Description</label>
                                    <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white" />
                                </div>
                                <label className="flex items-center gap-2 text-sm md:col-span-2">
                                    <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-saffron-500 w-4 h-4" />
                                    Show on public site
                                </label>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-slate-200 text-ink font-semibold text-sm">Cancel</button>
                                <button type="submit" className="px-5 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold text-sm" data-testid="admin-transfer-save">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, type = "text", value, onChange, required, placeholder }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}{required && " *"}</label>
        <input type={type} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white" />
    </div>
);

export default ManageTransfers;
